import React from "react";
import * as palette from "@govlaunch/palette";
import { DocumentIcon, LinkIcon } from "~/components/resource/icons";
import Dotdotdot from "~/components/text/Dotdotdot";
import { IFlatResource } from "~/types/types";
import { Anchor } from "@govlaunch/web";

interface ICard {
  href: string;
  download?: string | null;
  onRemove?: (args: any) => void;
  external?: "outside-govlaunch" | "govlaunch";
}

interface IInlineResource {
  onRemove?: (resource: IFlatResource) => void;
  resource: IFlatResource;
  color?: string;
}

const InlineResource: React.FunctionComponent<IInlineResource> = ({
  resource,
  onRemove,
  color = palette.disruptiveGreen,
}) => {
  if (resource.__typename === "DownloadResource") {
    return (
      <Card
        href={resource.url || ""}
        download={resource.filename}
        onRemove={() => (onRemove ? onRemove(resource) : undefined)}
      >
        <div
          css={{
            width: 24,
            height: 24,
            minWidth: 24,
            minHeight: 24,
          }}
        >
          <DocumentIcon
            width={24}
            height={24}
            color={color}
            style={{
              minWidth: 24,
              minHeight: 24,
            }}
          />
        </div>

        <div
          css={{
            marginLeft: 10,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <span
            css={{
              fontSize: 14,
              color: palette.darkGray,

              fontWeight: 500,
              lineHeight: 1.2,
              "&:hover": {
                color: palette.darkGray,
              },
            }}
          >
            <Dotdotdot clamp={2}>{resource.title}</Dotdotdot>
          </span>
        </div>
      </Card>
    );
  }

  if (resource.__typename === "LinkResource") {
    return (
      <Card
        href={resource.url || ""}
        onRemove={() => (onRemove ? onRemove(resource) : undefined)}
        external="outside-govlaunch"
      >
        <LinkIcon
          width={24}
          height={24}
          color={color}
          style={{
            minWidth: 24,
            minHeight: 24,
          }}
        />

        <div
          css={{
            marginLeft: 10,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <span
            css={{
              fontSize: 14,
              color: palette.darkGray,
              fontWeight: 500,
              lineHeight: 1.2,
              "&:hover": {
                color: palette.darkGray,
              },
            }}
          >
            <Dotdotdot clamp={2}>{resource.title}</Dotdotdot>
          </span>
        </div>
      </Card>
    );
  }

  return null;
};

const Card: React.FunctionComponent<ICard> = ({ children, href, download, external, onRemove }) => {
  return (
    <div
      css={{
        position: "relative",
      }}
    >
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          css={{
            color: palette.red,
            fontSize: 28,
            outline: 0,
            border: 0,
            background: "none",
            position: "absolute",
            top: 0,
            right: 2,
            zIndex: 100,
            cursor: "pointer",
          }}
        >
          &times;
        </button>
      )}

      <Anchor
        css={{
          padding: 16,
          display: "flex",
          alignItems: "center",
          position: "relative",
          boxShadow: "0 3px 11px 0 rgba(0, 0, 0, 0.1)",
          textDecoration: "none",
          borderRadius: 10,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.01)",
            boxShadow: "0 3px 11px 0 rgba(0, 0, 0, 0.15)",
          },
        }}
        href={href}
        download={download}
        external={external}
      >
        {children}
      </Anchor>
    </div>
  );
};

export default InlineResource;
